import React, {
  createContext, ReactNode, useContext, useEffect, useLayoutEffect, useMemo, useState,
} from 'react';
import { useColorMode } from '@chakra-ui/react';
import {
  Skin, LedgerTone, ColorPref, useSkin,
} from './SkinProvider';
import { LEDGER_TONES } from './brandTheme';
import { syncStealthFlag } from '../utils/money';

// Appearance preferences (item 61), surfaced on Tools → Appearance and persisted
// per-device in localStorage:
//  - skin:      'basix' = the Aurora "sexy web3" look (default) | 'accountant' = the
//               typewriter LEDGER skin (Courier Prime, off-cream paper, hard ink
//               strokes, fully monochrome). The skin itself lives in SkinProvider
//               (mounted ABOVE the ChakraProvider so the THEME can branch on it);
//               this provider just re-exposes it through useAppearance() so every
//               existing consumer keeps the same API.
//  - colorPref: 'light' | 'softLight' | 'softDark' | 'dark' | 'system' — the Basix
//               (Aurora) CANVAS tone. light/softLight pin Chakra light mode, dark/
//               softDark pin dark mode (the soft tones only soften the canvas colour,
//               not the mode); 'system' follows the OS and live-updates. Owned by
//               SkinProvider (above the ChakraProvider) so the built theme can paint
//               the chosen canvas — re-exposed here. In the Accountant skin the PAPER
//               TONE drives the colour mode instead, so colorPref is ignored there.

export type { Skin, LedgerTone, ColorPref };

interface AppearanceContextValue {
  skin: Skin;
  setSkin: (s: Skin) => void;
  // the Accountant paper tone (item 61) — only meaningful when skin === 'accountant'
  ledgerTone: LedgerTone;
  setLedgerTone: (t: LedgerTone) => void;
  colorPref: ColorPref;
  setColorPref: (c: ColorPref) => void;
  // convenience: true when the Aurora skin is active (gates DECORATION — glass, glow,
  // gradient text, the aurora backdrop, the number sweep)
  aurora: boolean;
  // item 94: gates FUNCTIONAL motion (count-up, hover-lift, entrance/panel transitions)
  // — true in BOTH skins, off only when the OS asks to reduce motion. So Accountant
  // stays flat/undecorated but no longer feels dead.
  motion: boolean;
  // item 327 — STEALTH mode: every money figure app-wide renders as ••• (demos /
  // client conversations). Per-device, instant on/off; utils/money.ts formatters
  // read the synced flag and this context update repaints every consumer.
  stealth: boolean;
  setStealth: (v: boolean) => void;
  // item 330 — EFFECTS tier for slower PCs: 'reduced' flattens the expensive GPU work
  // (backdrop blurs via the --bx-glass-* vars, the animated hero glow) while keeping
  // the brand look. Auto-detected once per device (low core-count / low memory →
  // reduced), overridable in Tools → Appearance. `fx` = convenience: effects are FULL.
  effects: 'full' | 'reduced';
  setEffects: (e: 'full' | 'reduced') => void;
  fx: boolean;
}

const AppearanceContext = createContext<AppearanceContextValue>({
  skin: 'basix',
  setSkin: () => {},
  ledgerTone: 'soft',
  setLedgerTone: () => {},
  colorPref: 'dark',
  setColorPref: () => {},
  aurora: true,
  motion: true,
  stealth: false,
  setStealth: () => {},
  effects: 'full',
  setEffects: () => {},
  fx: true,
});

const STEALTH_KEY = 'basix.stealth';
const EFFECTS_KEY = 'basix.effects';

// item 330 — one-time hardware sniff: few cores or little RAM → default to reduced
// effects. Heuristic only (Safari hides deviceMemory) — the user can always override.
const detectDefaultEffects = (): 'full' | 'reduced' => {
  try {
    const cores = navigator.hardwareConcurrency ?? 8;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const mem = (navigator as any).deviceMemory ?? 8;
    return cores <= 4 || mem <= 4 ? 'reduced' : 'full';
  } catch {
    return 'full';
  }
};

export const AppearanceProvider = ({ children }: { children: ReactNode }) => {
  const { setColorMode } = useColorMode();
  // skin, paper tone AND the Basix colour preference are owned by SkinProvider (above
  // the ChakraProvider, so the theme can be built from them); we just re-expose them.
  const {
    skin, setSkin, ledgerTone, setLedgerTone, colorPref, setColorPref,
  } = useSkin();
  // item 94: honour the OS reduce-motion setting (live), independent of skin
  const [reduceMotion, setReduceMotion] = useState<boolean>(
    () => typeof window !== 'undefined'
      && !!window.matchMedia?.('(prefers-reduced-motion: reduce)').matches,
  );
  // item 327 — stealth mode, per-device. The module flag in utils/money.ts is synced
  // SYNCHRONOUSLY (initializer + setter) so formatters read the right value in the
  // very render this context update triggers.
  const [stealth, setStealthState] = useState<boolean>(() => {
    let v = false;
    try { v = window.localStorage.getItem(STEALTH_KEY) === 'true'; } catch { /* no storage */ }
    syncStealthFlag(v);
    return v;
  });
  const setStealth = (v: boolean) => {
    syncStealthFlag(v);
    try { window.localStorage.setItem(STEALTH_KEY, String(v)); } catch { /* no storage */ }
    setStealthState(v);
  };
  // item 330 — effects tier (saved choice wins; else the hardware sniff decides)
  const [effects, setEffectsState] = useState<'full' | 'reduced'>(() => {
    try {
      const saved = window.localStorage.getItem(EFFECTS_KEY);
      if (saved === 'full' || saved === 'reduced') return saved;
    } catch { /* no storage */ }
    return detectDefaultEffects();
  });
  const setEffects = (e: 'full' | 'reduced') => {
    try { window.localStorage.setItem(EFFECTS_KEY, e); } catch { /* no storage */ }
    setEffectsState(e);
  };
  // reduced mode flattens every glass surface at the source: an inline root style
  // overrides the themed --bx-glass-* vars (element style beats the stylesheet), so
  // all 16 blur consumers go flat without touching any of them.
  useLayoutEffect(() => {
    const root = document.documentElement.style;
    if (effects === 'reduced' && skin === 'basix') {
      root.setProperty('--bx-glass-panel', 'none');
      root.setProperty('--bx-glass-chip', 'none');
      root.setProperty('--bx-glass-veil', 'none');
    } else {
      root.removeProperty('--bx-glass-panel');
      root.removeProperty('--bx-glass-chip');
      root.removeProperty('--bx-glass-veil');
    }
  }, [effects, skin]);

  // apply the colour preference to Chakra. In the Accountant skin the PAPER TONE
  // owns the colour mode (brilliant/soft → light, softDark/dark → dark), so the
  // mode-based component bits resolve to match the chosen paper. In Basix, colorPref
  // drives it: light/softLight pin LIGHT, dark/softDark pin DARK (the soft tones only
  // change the canvas colour, not the light/dark mode), and 'system' tracks the OS live.
  // useLayoutEffect (not useEffect) so the colour mode flips in the SAME commit the
  // theme rebuilds — otherwise switching skin/paper showed a one-frame mismatch
  // ("didn't take on the first try"): new paper, stale light/dark component bits.
  useLayoutEffect(() => {
    if (skin === 'accountant') {
      setColorMode(LEDGER_TONES[ledgerTone].mode);
      return undefined;
    }
    if (colorPref !== 'system') {
      setColorMode(colorPref === 'light' || colorPref === 'softLight' ? 'light' : 'dark');
      return undefined;
    }
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const apply = () => setColorMode(mq.matches ? 'dark' : 'light');
    apply();
    mq.addEventListener?.('change', apply);
    return () => mq.removeEventListener?.('change', apply);
  }, [skin, ledgerTone, colorPref, setColorMode]);

  // track OS reduce-motion live (item 94)
  useEffect(() => {
    const mq = window.matchMedia?.('(prefers-reduced-motion: reduce)');
    if (!mq) return undefined;
    const apply = () => setReduceMotion(mq.matches);
    apply();
    mq.addEventListener?.('change', apply);
    return () => mq.removeEventListener?.('change', apply);
  }, []);

  const value = useMemo<AppearanceContextValue>(
    () => ({
      skin,
      setSkin,
      ledgerTone,
      setLedgerTone,
      colorPref,
      setColorPref,
      aurora: skin === 'basix',
      // reduced effects also stills FUNCTIONAL motion — animation is GPU work too
      motion: !reduceMotion && effects === 'full',
      stealth,
      setStealth,
      effects,
      setEffects,
      fx: effects === 'full',
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [skin, setSkin, ledgerTone, setLedgerTone, colorPref, setColorPref, reduceMotion, stealth, effects],
  );

  return <AppearanceContext.Provider value={value}>{children}</AppearanceContext.Provider>;
};

export const useAppearance = (): AppearanceContextValue => useContext(AppearanceContext);

export default AppearanceProvider;
